import { useState, useRef, useEffect, useMemo } from 'react';

// Searchable person picker. Filters a provided list client-side (the candidate
// set is small and already loaded). Value/onChange speak the integer UserId
// (TeamMemberId) so it's a drop-in for the old <select>. `currentLabel` lets an
// already-assigned-but-now-inactive user still display even when they're no
// longer in `options` (disabled users are excluded from the pick-list).
export default function PersonAutocomplete({ value, options = [], onChange, currentLabel, placeholder = 'Search…' }) {
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);

  const selected = options.find(o => String(o.TeamMemberId) === String(value));
  const selectedLabel = selected ? selected.FullName : (value ? (currentLabel || '') : '');

  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setFocused(false); }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => (o.FullName || '').toLowerCase().includes(q)) : options;
  }, [query, options]);

  function pick(o) { onChange(o ? o.TeamMemberId : null); setOpen(false); setFocused(false); setQuery(''); }

  function onKeyDown(e) {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (activeIdx >= 0 && filtered[activeIdx]) pick(filtered[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={focused ? query : selectedLabel}
        placeholder={selectedLabel || placeholder}
        autoComplete="off"
        onFocus={() => { setFocused(true); setQuery(''); setOpen(true); setActiveIdx(-1); }}
        onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
        onKeyDown={onKeyDown}
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {open && (
        <div className="autocomplete-dropdown">
          {value ? (
            <div className="autocomplete-option" onMouseDown={() => pick(null)}>
              <span style={{ color: 'var(--color-gray-500)' }}>— Clear —</span>
            </div>
          ) : null}
          {filtered.length === 0 ? (
            <div className="autocomplete-option" style={{ color: 'var(--color-gray-400)', cursor: 'default' }}>No matches</div>
          ) : filtered.map((o, i) => (
            <div
              key={o.TeamMemberId}
              className={`autocomplete-option ${i === activeIdx ? 'active' : ''}`}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={() => pick(o)}
            >
              <div className="autocomplete-option-name">{o.FullName}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
